import React, { useState } from "react";
import { Navigate } from 'react-router-dom';
import { TableRow, TableCell } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, UserCheck, UserRoundX } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function getStatusBadge(requestStatus, domainStatus) {
    if (requestStatus === "Approved" && domainStatus === true) {
        return <Badge variant="success">Approved</Badge>;
    }
    if (requestStatus === "Submitted" && domainStatus === true) {
        return <Badge variant="success">Active</Badge>;
    }
    if (requestStatus === "Submitted" && domainStatus === false) {
        return <Badge variant="secondary">Inactive</Badge>;
    }
    if (requestStatus === "Pending") {
        return <Badge variant="warning">Pending</Badge>;
    }
    if (requestStatus === "Rejected") {
        return <Badge variant="destructive">Rejected</Badge>;
    }
    return null;
}

function Users_no_row({ fullName, groupNumber, requestId, requestStatus, subDomain, userId, domainStatus, userName, pricing_action, activate_deactivate_action }) {
    const [redirectUser, setRedirectUser] = useState('');

    if (redirectUser !== '') {
        return <Navigate replace to={{ pathname: "/Admin/users", state: { property_id: redirectUser } }} />;
    }

    if (!fullName) {
        return (
            <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No Record Found
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className="hover:bg-gray-50">
            <TableCell>
                <Button
                    type="button"
                    variant="link"
                    onClick={(e) => { e.preventDefault(); setRedirectUser(userName); }}
                    className="text-primary hover:text-accent font-medium p-0 h-auto"
                >
                    {fullName}
                </Button>
            </TableCell>
            <TableCell className="text-primary">{groupNumber}</TableCell>
            <TableCell className="max-w-[220px]">
                <a
                    href={subDomain}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={subDomain}
                    className="text-primary hover:text-accent inline-flex items-center gap-1 transition-colors max-w-full min-w-0"
                >
                    <span className="truncate">{subDomain}</span>
                    <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
            </TableCell>
            <TableCell>
                {getStatusBadge(requestStatus, domainStatus)}
            </TableCell>
            <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                    {requestStatus === "Pending" && domainStatus === true && (
                        <>
                            <Button
                                size="sm"
                                variant="success"
                                onClick={() => pricing_action(userId, requestId, "Accept")}
                            >
                                Accept
                            </Button>
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => pricing_action(userId, requestId, "Reject")}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                    {requestStatus === "Submitted" && domainStatus === true && (
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => activate_deactivate_action(userId, requestId, "Deactivate")}
                                        className="h-8 w-8 p-0"
                                    >
                                        <UserRoundX className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Deactivate Tool</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    {requestStatus === "Submitted" && domainStatus === false && (
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => activate_deactivate_action(userId, requestId, "Activate")}
                                        className="h-8 w-8 p-0"
                                    >
                                        <UserCheck className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Activate Tool</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </TableCell>
        </TableRow>
    );
}

// --- Mobile card -------------------------------------------------------------
export function PricingCard({ fullName, groupNumber, requestId, requestStatus, subDomain, userId, domainStatus, userName, pricing_action, activate_deactivate_action }) {
    const [redirectUser, setRedirectUser] = useState('');

    if (redirectUser !== '') {
        return <Navigate replace to={{ pathname: "/Admin/users", state: { property_id: redirectUser } }} />;
    }

    if (!fullName) return null;

    return (
        <Card>
            <CardContent className="p-4 space-y-3">
                {/* Header: name + status */}
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <Button
                            type="button"
                            variant="link"
                            onClick={(e) => { e.preventDefault(); setRedirectUser(userName); }}
                            className="font-semibold text-sm text-primary hover:text-accent p-0 h-auto text-left truncate block"
                        >
                            {fullName}
                        </Button>
                    </div>
                    <div className="shrink-0">{getStatusBadge(requestStatus, domainStatus)}</div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm border-t pt-3">
                    <div>
                        <p className="text-xs text-muted-foreground">Group Number</p>
                        <p className="font-medium mt-0.5">{groupNumber || '-'}</p>
                    </div>
                    <div className="col-span-2 min-w-0 overflow-hidden">
                        <p className="text-xs text-muted-foreground">Domain</p>
                        {subDomain ? (
                            <a
                                href={subDomain}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={subDomain}
                                className="inline-flex items-center gap-1 text-accent hover:text-accent/80 font-medium mt-0.5 text-sm max-w-full min-w-0"
                            >
                                <span className="break-all">{subDomain}</span>
                                <ExternalLink className="h-3 w-3 shrink-0" />
                            </a>
                        ) : (
                            <p className="font-medium mt-0.5">-</p>
                        )}
                    </div>
                </div>

                {/* Actions */}
                {(requestStatus === "Pending" && domainStatus === true) && (
                    <div className="flex gap-2 border-t pt-3">
                        <Button
                            size="sm"
                            variant="success"
                            className="flex-1"
                            onClick={() => pricing_action(userId, requestId, "Accept")}
                        >
                            Accept
                        </Button>
                        <Button
                            size="sm"
                            variant="destructive"
                            className="flex-1"
                            onClick={() => pricing_action(userId, requestId, "Reject")}
                        >
                            Reject
                        </Button>
                    </div>
                )}
                {requestStatus === "Submitted" && domainStatus === true && (
                    <div className="flex border-t pt-3">
                        <Button
                            size="sm"
                            variant="outline"
                            className="gap-2"
                            onClick={() => activate_deactivate_action(userId, requestId, "Deactivate")}
                        >
                            <UserRoundX className="h-4 w-4" />
                            Deactivate Tool
                        </Button>
                    </div>
                )}
                {requestStatus === "Submitted" && domainStatus === false && (
                    <div className="flex border-t pt-3">
                        <Button
                            size="sm"
                            variant="outline"
                            className="gap-2"
                            onClick={() => activate_deactivate_action(userId, requestId, "Activate")}
                        >
                            <UserCheck className="h-4 w-4" />
                            Activate Tool
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default Users_no_row;


